import { inject, Injectable } from '@angular/core';
import {Observable, forkJoin, of} from 'rxjs';
import { catchError, defaultIfEmpty, first, map } from 'rxjs/operators';
import { ACCOUNT_API_REPOSITORY } from '../../domain/tokens/account-api.token';
import { ACCOUNT_STORAGE_REPOSITORY } from '../../domain/tokens/account-storage.token';
import { DeviceService } from '../../../device/device.service';
import { Logger } from 'src/app/core/interfaces/Logger';
import { Account } from '../../domain/model/account.model';
import { mergeAccounts } from '../../utils/account-processor.utils';
import { IQueryParams } from 'src/app/core/interfaces/query-params.interface';

@Injectable()
export class AccountSyncService {
  private readonly logger = inject(Logger);
  private readonly device = inject(DeviceService);
  private readonly api = inject(ACCOUNT_API_REPOSITORY);
  private readonly storage = inject(ACCOUNT_STORAGE_REPOSITORY);

  getFromServer(): Observable<Account[]> {
    const queryParams = this.buildQueryParams();

    return this.api.getAll(queryParams).pipe(
      first(),
      catchError(error => this.handleError('getFromServer', error, []))
    );
  }

  getFromStorage(): Observable<Account[]> {
    return this.storage.getAll().pipe(
      defaultIfEmpty([]),
      first(),
      map(accounts => this.sortByCreationDate(accounts)),
      catchError(error => this.handleError('getFromStorage', error, []))
    );
  }

  saveToStorage(accounts: Account[]): Observable<Account[]> {
    return this.storage.save(accounts).pipe(
      first(),
      catchError(error => this.handleError('saveToStorage', error, []))
    );
  }

  clearStorage(): Observable<void> {
    return this.storage.clear().pipe(
      catchError(error => this.handleError('clearStorage', error, undefined))
    );
  }

  syncServerWithStorage(): Observable<Account[]> {
    return forkJoin({
      serverAccounts: this.getFromServer(),
      localAccounts: this.getFromStorage()
    }).pipe(
      map(({ serverAccounts, localAccounts }) => mergeAccounts(serverAccounts, localAccounts)),
      catchError(error => this.handleError('syncServerWithStorage', error, []))
    );
  }

  private sortByCreationDate(accounts: Account[]): Account[] {
    return [...accounts].sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
  }

  private buildQueryParams(): IQueryParams {
    return {
      sort: { field: 'created_at', order: 'asc' },
      filter: { device_id: this.device.device?._id ?? '' },
    };
  }

  private handleError<T>(operation: string, error: unknown, fallbackValue: T): Observable<T> {
    this.logger.error(new Error(`[AccountRepository][${operation}]`), error);

    return of(fallbackValue as T);
  }
}
